import { useState } from 'react';
import useApiFetch from '@/hooks/useApiFetch';
import useProfile from '@/hooks/useProfileV2';
import ptosServices from '@/services/ptos';
import { FetchApiIds } from '@/types';
import { PTORequest, PTOType } from '@/types/api';
import { StaffMember } from '@/types/api/staffMember';
import { createBalanceByPTOType } from '@/utils/pto';
import MyTimeOffsPageContentComponent, {
  type Props as ComponentProps,
} from './MyTimeOffsPageContent.component';

type Props = Pick<ComponentProps, 'adminMode'> & {
  staffMember: StaffMember;
  prefilledPTOType?: PTOType;
  requestInitialValue?: boolean;
};

const ITEMS_PER_PAGE = 10;

const MyTimeOffsPageContent = ({
  staffMember,
  requestInitialValue = false,
  adminMode,
  ...props
}: Props): JSX.Element => {
  const [prefilledPTOType, setPrefilledPTOType] = useState<PTOType | undefined>(
    props.prefilledPTOType
  );
  const [editingRequest, setEditingRequest] = useState<PTORequest | undefined>();
  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(requestInitialValue);
  const [page, setPage] = useState<number>(1);
  const [isAdminEditOpen, setIsAdminEditOpen] = useState<boolean>(false);
  const { profile } = useProfile();
  const [answerViewerRequest, setAnswerViewerRequest] = useState<PTORequest>();
  const [showAnswerViewerModal, setShowAnswerViewerModal] = useState<boolean>(false);

  const {
    data: getBalanceResponse,
    isLoading: isBalanceLoading,
    refetch: refetchBalance,
  } = useApiFetch([FetchApiIds.GET_BANK_OF_HOURS_BALANCE, staffMember.id], async () =>
    ptosServices.getBankOfHoursBalance({
      staffMemberId: staffMember.id,
    })
  );

  const {
    data: getPTOsResponse,
    isLoading: isLoadingPtos,
    refetch: refetchPTOs,
  } = useApiFetch(
    [FetchApiIds.GET_PTOS_BY_STAFF_MEMBER, staffMember?.id, page],
    async () =>
      ptosServices.getPTOsByStaffMember({
        staffMemberId: staffMember?.id || '',
        page,
        take: ITEMS_PER_PAGE,
      }),
    { enabled: Boolean(staffMember) }
  );

  const balanceObject = createBalanceByPTOType(getBalanceResponse || []);

  const handleUpdatePTOs = (): void => {
    refetchPTOs();
    refetchBalance();
  };

  const handlePrefilledRequestClick = (ptoType?: PTOType): void => {
    setPrefilledPTOType?.(ptoType);
    setEditingRequest?.(undefined);
    setIsEditModalOpen(true);
  };

  const handleEditRequest = (request: PTORequest): void => {
    setPrefilledPTOType?.(undefined);
    setEditingRequest?.(request);
    setIsEditModalOpen(true);
  };

  const handleAdminEditRequest = (request: PTORequest): void => {
    setEditingRequest?.(request);
    setIsAdminEditOpen(true);
  };

  const handleViewAnswerNoteClick = (request: PTORequest): void => {
    setAnswerViewerRequest(request);
    setShowAnswerViewerModal(true);
  };

  return (
    <MyTimeOffsPageContentComponent
      balance={balanceObject}
      onRequestClick={handlePrefilledRequestClick}
      isLoadingPTOs={isLoadingPtos}
      ptos={getPTOsResponse?.data}
      onEditRequestClick={handleEditRequest}
      lastPage={getPTOsResponse?.lastPage}
      currentPage={page}
      totalPTOs={getPTOsResponse?.count}
      itemsPerPage={ITEMS_PER_PAGE}
      onPageChange={setPage}
      prefilledPTOType={prefilledPTOType}
      editingRequest={editingRequest}
      isRequestedModalOpen={isEditModalOpen}
      onCloseModal={() => setIsEditModalOpen(false)}
      onPTOsUpdate={handleUpdatePTOs}
      adminMode={adminMode}
      profile={profile}
      isAdminEditOpen={isAdminEditOpen}
      onAdminEdit={handleAdminEditRequest}
      onAdminEditClose={() => setIsAdminEditOpen(false)}
      isBalanceLoading={isBalanceLoading}
      onViewAnswerNoteClick={handleViewAnswerNoteClick}
      showAnswerViewerModal={showAnswerViewerModal}
      onCloseAnswerViewerModal={() => setShowAnswerViewerModal(false)}
      answerViewerRequest={answerViewerRequest}
    />
  );
};

export default MyTimeOffsPageContent;
